/**
 * @file index.tsx
 *
 * @fileoverview Creates a generic layout to be reused on pages, including Header, main content
 * and footer.
 */
import React, { useContext } from 'react';
import { UserContextInterface, UserContext } from '~/jobiqo/context/user';
import { isApplicant, isRecruiter } from '~/jobiqo/lib/auth';
import AnonymousLayout from './Anonymous';
import RecruiterLayout from '~/jobiqo/components/Layout/Recruiter';
import ApplicantLayout from '~/jobiqo/components/Layout/Applicant';

interface LayoutProps {
  /**
   * The content to display inside the layout container.
   */
  children: React.ReactNode;
  /**
   * True if the main content should be full width.
   */
  fullWidthContainer?: boolean;
  /**
   * Main content size.
   */
  mainContentSize?: 'small' | 'medium' | 'large' | 'xLarge';
  /**
   * Fixed header.
   *
   * @default false.
   *
   */
  fixedHeader?: boolean;
  /**
   * Hide the site header while keeping the rest of the layout intact.
   *
   * @default false.
   */
  hideHeader?: boolean;
}

export const Layout = (props: LayoutProps) => {
  const userContext: UserContextInterface = useContext(UserContext);

  if (
    !userContext.user ||
    (!isRecruiter(userContext.user) && !isApplicant(userContext.user))
  ) {
    return <AnonymousLayout {...props} />;
  }

  return userContext.user && isRecruiter(userContext.user) ? (
    <RecruiterLayout {...props} />
  ) : (
    <ApplicantLayout {...props} />
  );
};

export default Layout;
